import { intervalToDuration } from "date-fns";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { StudioMemberWatchEvent } from "@prisma/client";

export async function fetcher<JSON = any>(
  input: RequestInfo,
  init?: RequestInit,
): Promise<JSON> {
  const response = await fetch(input, { ...init, cache: "no-store" });

  return response.json();
}

export const capitalize = (s: string) => {
  if (typeof s !== "string") return "";
  return s
    .split(" ")
    .map((word) => {
      if (word.length === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

export const getInitials = (name: string): string =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

export const upperCase = (s: string) => {
  if (typeof s !== "string") return "";
  return s.toUpperCase();
};

export const formatCurrency = (
  amount: number,
  currency: string = "USD",
): string => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(amount);
};

export const truncate = (str: string, num: number) => {
  if (!str) return "";
  if (str.length <= num) {
    return str;
  }
  return str.slice(0, num) + "...";
};

export const toSentenceCase = (str: string) => {
  str = str.trim();

  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getBlurDataURL = async (url: string | null) => {
  if (!url) {
    return "data:image/webp;base64,AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=";
  }
  try {
    const response = await fetch(
      `https://wsrv.nl/?url=${url}&w=50&h=50&blur=5`,
    );
    const buffer = await response.arrayBuffer();
    const base64 = Buffer.from(buffer).toString("base64");

    return `data:image/png;base64,${base64}`;
  } catch (error) {
    return "data:image/webp;base64,AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=";
  }
};

export const placeholderBlurhash =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAAAoJJREFUWEfFl4lu4zAMRO3cx/9/au6reMaOdkxTTl0grQFCRoqaT+SQotq2bV9N8rRt28xms87m83l553eZ/9vr9Wpkz+ezkT0ej+6dv1X81AFw7M4FBACPVn2c1Z3zLgDeJwHgeLFYdAARYioAEAKJEG2WAjl3gCwNYymQQ9b7/V4spmIAwO6Wy2VnAMikBWlDURBELf8CuN1uHQSrPwMAHK5WqwFELQ01AIXdAa7XawfAb3p6AOwK5+v1ugAoEq4FRSFLgavfQ49jAGQpAE5wjgGCeRrGdBArwHOPcwFcLpcGU1X0IsBuN5tNgYhaiFFwHTiAwq8I+O5xfj6fOz38K+X/fYAdb7fbAgFAjIJ6Aav3AYlQ6nfnDoDz0+lUxNiLALvf7XaDNGQ6GANQBKR85V27B4D3QQRw7hGIYlQKWGM79hSweyCUe1blXhEAogfABwHAXAcqSYkxCtHLUK3XBajSc4Dj8dilAeiSAgD2+30BAEKV4GKcAuDqB4TdYwBgPQByCgApUBoE4EJUGvxUjF3Q69/zLw3g/HA45ABKgdIQu+JPIyDnisCfAxAFNFM0EFNQ64gfS0EUoQP8ighrZSjn3oziZEQpauyKbfjbZchHUL/3AS/Dd30gAkxuRACgfO+EWQW8qwI1o+wseNuKcQiESjALvwNoMI0TcRzD4lFcPYwIM+JTF5x6HOs8yI7jeB5oKhpMRFH9UwaSCDB2Jmg4rc6E2TT0biIaG0rQhNqyhpHBcayTTSXH6vcDL7/sdqRK8LkwTsU499E8vRcAojHcZ4AxABdilgrp4lsXk8oVqgwh7+6H3phqd8J0Kk4vbx/+sZqCD/vNLya/5dT9fAH8g1WdNGgwbQAAAABJRU5ErkJggg==";

export const toDateString = (date: Date) => {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const dateFormat = (
  startDate?: any | Date,
  endDate?: any | Date,
): string => {
  const formatDate = (date: any | Date): string => {
    const dateObj = new Date(date);
    const dateOptions: Intl.DateTimeFormatOptions = {
      weekday: "long",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    const dateString = dateObj.toLocaleString("en-US", dateOptions);
    const timeString = dateObj.toLocaleTimeString("en-US", timeOptions);
    const [weekday, month, day] = dateString.replace(",", "").split(" ");
    const dayWithSuffix = `${parseInt(day)}${getOrdinalSuffix(parseInt(day))}`;
    return `${weekday}, ${month} ${dayWithSuffix}, from ${timeString}`;
  };

  const formatTime = (date: Date): string => {
    const dateObj = new Date(date);
    const options: Intl.DateTimeFormatOptions = {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    const timeString = dateObj.toLocaleTimeString("en-US", options);
    return `to ${timeString}`;
  };

  const startDateString = startDate ? formatDate(startDate) : "";
  const timeString = endDate ? formatTime(endDate) : "";

  return timeString ? `${startDateString} ${timeString}` : startDateString;
};

export const formatDate = (date: string | Date): string => {
  const dateObj = new Date(date);
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  const dateString = dateObj.toLocaleString("en-US", options);
  const [weekday, month, day, time] = dateString.replace(",", "").split(" ");
  const dayWithSuffix = `${parseInt(day)}${getOrdinalSuffix(parseInt(day))}`;
  return `${weekday} ${month} ${dayWithSuffix} • ${time}`;
};

export const random = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const dataURItoBlob = (dataURI: string) => {
  const binary = atob(dataURI.split(",")[1]);
  const array = [];

  for (let i = 0; i < binary.length; i++) {
    // @ts-ignore
    array.push(binary?.charCodeAt(i) as number);
  }

  return new Blob([new Uint8Array(array)], { type: "image/png" });
};

export const formatBytes = (bytes: number, decimals = 2 as number) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export function formatDuration(seconds: number) {
  if (seconds === 0) {
    return "00:00";
  }

  const duration = intervalToDuration({ start: 0, end: seconds * 1000 });

  const formattedSeconds =
    duration.seconds === undefined
      ? "00"
      : duration.seconds < 10
        ? `0${duration.seconds}`
        : `${duration.seconds}`;
  const formattedMinutes =
    duration.minutes === undefined || duration.minutes === null
      ? "00"
      : duration.minutes < 10
        ? `0${duration.minutes}`
        : `${duration.minutes}`;

  if (duration.hours && duration.hours > 0) {
    return `${duration.hours}:${formattedMinutes}:${formattedSeconds}`;
  }

  return `${formattedMinutes}:${formattedSeconds}`;
}

export const formatEventDuration = (startDate: Date, endDate: Date): string => {
  const durationInMilliseconds = endDate.getTime() - startDate.getTime();
  const durationInMinutes = Math.floor(durationInMilliseconds / 60000);
  const hours = Math.floor(durationInMinutes / 60);
  const minutes = durationInMinutes % 60;

  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");

  return `${formattedHours} h ${formattedMinutes} min`;
};

export const formatCountdown = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours}h ${minutes}m ${seconds}s`;
};

export const lightenColor = (color: string, percent: number) => {
  const num = parseInt(color.replace("#", ""), 16),
    amt = Math.round(2.55 * percent),
    R = (num >> 16) + amt,
    G = ((num >> 8) & 0x00ff) + amt,
    B = (num & 0x0000ff) + amt;
  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

export const formatPrice = (price: number, currencySymbol: string) => {
  return `${currencySymbol}${price.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export function unpluralize(word: string): string {
  if (word.endsWith("ies")) {
    return word.slice(0, -3) + "y";
  } else if (word.endsWith("s") && word.length > 1) {
    return word.slice(0, -1);
  }
  return word;
}

export const getImageType = (url?: string | null) => {
  if (!url) return null;
  return url.split(".").pop();
};

export const getBase64Image = async (url?: string | null) => {
  if (!url) return null;

  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  const base64 = Buffer.from(arrayBuffer).toString("base64");
  return base64;
};

export function extractUrls(text: string): string[] {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.match(urlRegex) || [];
}

export function filterUniqueWatchEvents(
  events: StudioMemberWatchEvent[],
): (StudioMemberWatchEvent & { playCount: number })[] {
  const videoMap = new Map<
    string,
    {
      event: StudioMemberWatchEvent;
      playCount: number;
      hasCompleted: boolean;
    }
  >();

  for (const event of events) {
    const videoId = event?.videoId as string;
    const currentData = videoMap.get(videoId);
    const isCompleted = event.percentWatched === 100;

    if (!currentData) {
      videoMap.set(videoId, {
        event,
        playCount: 1,
        hasCompleted: isCompleted,
      });
    } else {
      // If we find a completed watch, use that event
      if (isCompleted && !currentData.hasCompleted) {
        videoMap.set(videoId, {
          event,
          playCount: currentData.playCount + 1,
          hasCompleted: true,
        });
      } else if (!isCompleted && !currentData.hasCompleted) {
        // If neither are completed, use the one with highest percentage
        if (
          (event.percentWatched || 0) > (currentData.event.percentWatched || 0)
        ) {
          videoMap.set(videoId, {
            event,
            playCount: currentData.playCount + 1,
            hasCompleted: false,
          });
        }
      }
      // Increment play count regardless
      videoMap.get(videoId)!.playCount++;
    }
  }

  return Array.from(videoMap.values()).map(({ event, playCount }) => ({
    ...event,
    playCount,
  }));
}

/** Watched at or beyond this percent counts as finished — replay from the start. */
const WATCH_COMPLETE_PERCENT = 95;
/** A saved position this close to the end also counts as finished. */
const WATCH_COMPLETE_TAIL_SECONDS = 5;

/**
 * Seconds to resume a video from, or undefined to start at 0. Finished videos
 * restart rather than resume at the very end.
 *
 * `percentWatched` alone can't be trusted: recordWatchEvent keeps the furthest
 * `videoPosition` ever reached but the latest `percentWatched`, so a rewatch
 * leaves the position at the end with a low percent. Resuming there fires
 * `ended` before the member presses play — the video "can't be replayed" and a
 * playlist skips ahead (TRA-2990). Pass the video's duration to guard on the
 * position itself.
 */
export function getResumePosition(
  event?: Pick<
    StudioMemberWatchEvent,
    "videoPosition" | "percentWatched"
  > | null,
  duration?: number | null,
): number | undefined {
  const position = event?.videoPosition ?? 0;
  if (position <= 0) return undefined;
  if ((event?.percentWatched ?? 0) >= WATCH_COMPLETE_PERCENT) return undefined;
  if (duration && duration > 0) {
    const isNearEnd =
      position >= duration - WATCH_COMPLETE_TAIL_SECONDS ||
      (position / duration) * 100 >= WATCH_COMPLETE_PERCENT;
    if (isNearEnd) return undefined;
  }
  return Math.floor(position);
}

export function formatMs(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
